import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import { useNavigate } from 'react-router';

import { Dialog, Transition } from '@headlessui/react';
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { useCart } from '@boc/framework/checkout/cart';
import { getAttribute } from '@boc/framework/products/attributes';
import { groupPuzzlesByShape } from '~/local/custom-puzzle/utils';
import type { ProductDetailsFragment } from '@boc/framework/saleor-graphql';

import { cn } from '~/styling';
import { urls } from '~/urls';

import { buttonVariants } from '~/local/components/button';
import { Price } from '~/local/components/price';
import type { ThemeId } from '~/local/custom-map-puzzle';
import { areaConfig, areaKind, areaRequestParams, mapSummary, previewAspect } from '~/local/custom-map-puzzle/area';
import { AreaOptions, DEFAULT_AREA_OPTIONS } from '~/local/custom-map-puzzle/components/area-options';
import type { AreaOptionsValue } from '~/local/custom-map-puzzle/components/area-options';
import { DEFAULT_DISTANCE, DistanceSlider } from '~/local/custom-map-puzzle/components/distance-slider';
import type { FontId } from '~/local/custom-map-puzzle/components/font-selector';
import { DEFAULT_FONT, FontSelector } from '~/local/custom-map-puzzle/components/font-selector';
import { MapPreview } from '~/local/custom-map-puzzle/components/map-preview';
import type { PlaceResult } from '~/local/custom-map-puzzle/components/place-autocomplete';
import { PlaceAutocomplete } from '~/local/custom-map-puzzle/components/place-autocomplete';
import { ThemeSelector } from '~/local/custom-map-puzzle/components/theme-selector';
import type { MapPuzzleMetadata } from '~/local/custom-map-puzzle/types';
import { requestMapPoster } from '~/local/custom-map-puzzle/request-map-poster';
import { getQualityForSlug, stringifyMapPuzzleMetadata } from '~/local/custom-map-puzzle/types';

const getPieces = (variant: ProductDetailsFragment) => {
  const regexPieces = new RegExp(/(?<pieces>\d+) Piece/);
  const matchPieces = variant.name.match(regexPieces)?.groups?.pieces;

  return matchPieces ? Number(matchPieces) : 0;
};

interface MapPuzzleFlowSheetProps {
  isOpen: boolean;
  onClose: () => void;
  products: ProductDetailsFragment[];
  params: {
    locale: string;
    channel: string;
  };
  initialSlug?: string;
  /** Open with this place chosen: the world, from the landing page's "Map the Whole World" */
  initialPlace?: PlaceResult | null;
}

// Step Section Wrapper
const StepSection = ({
  number,
  title,
  completed,
  children,
}: {
  number: number;
  title: string;
  completed: boolean;
  children: React.ReactNode;
}) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium',
            completed ? 'bg-green-100 text-green-700' : 'bg-primary-2-10 text-primary-2-80'
          )}
        >
          {completed ? <CheckCircleIcon className="h-5 w-5" /> : number}
        </div>
        <h3 className="font-heading-condensed text-primary-2-80 text-lg font-medium">
          {title}
        </h3>
      </div>
      <div className="ml-11">{children}</div>
    </div>
  );
};

// Step 1: Select Puzzle Type
const SelectPuzzleType = ({
  products,
  selectedSlug,
  onSelect,
  scrollToMap,
}: {
  products: ProductDetailsFragment[];
  selectedSlug: string | undefined;
  onSelect: (slug: string) => void;
  scrollToMap?: () => void;
}) => {
  const sortedProducts = products
    .filter((p) => p.isAvailable)
    .sort((a, b) => getPieces(a) - getPieces(b));

  const handleSelect = (slug: string) => {
    onSelect(slug);
    setTimeout(() => {
      scrollToMap?.();
    }, 100);
  };

  return (
    <div className="space-y-4">
      {groupPuzzlesByShape(sortedProducts).map((group) => (
        <div key={group.shape} data-testid={`puzzle-shape-${group.shape.toLowerCase()}`}>
          <div className="text-neutral-2-60 mb-2 text-xs font-medium tracking-wide uppercase">
            {group.shape}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {group.products.map((product) => {
              const isSelected = selectedSlug === product.slug;

              return (
                <button
                  data-testid={`puzzle-type-${product.slug}`}
                  key={product.slug}
                  onClick={() => handleSelect(product.slug)}
                  className={cn(
                    'rounded-lg border p-3 text-left transition-all',
                    isSelected
                      ? 'border-primary-2 bg-primary-2-10'
                      : 'border-neutral-2-20 hover:border-neutral-2-40 bg-white'
                  )}
                >
                  <div className="text-neutral-2-80 text-sm font-medium">
                    {product.name.replace('Piece', 'pc')}
                  </div>
                  <div className="text-neutral-2-60 mt-1 text-xs">
                    ~{Math.round(Number(getAttribute(product.attributes, 'length')?.values[0].name))} x{' '}
                    {Math.round(Number(getAttribute(product.attributes, 'width')?.values[0].name))} cm
                  </div>
                  {product.variants && (
                    <div className="text-primary-2-80 mt-2 font-medium">
                      <Price
                        label="price"
                        price={product.variants[0].pricing?.price?.gross}
                        truncate={true}
                      />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// Step 2: Configure Map
const DEBOUNCE_MS = 800;

const ConfigureMap = ({
  selectedProduct,
  params,
  onSuccess,
  onProceedToCheckout,
  initialPlace,
}: {
  selectedProduct: ProductDetailsFragment;
  params: { locale: string; channel: string };
  onSuccess: (checkoutId: string) => void;
  onProceedToCheckout: () => void;
  initialPlace?: PlaceResult | null;
}) => {
  const { addItem, refetch } = useCart();

  const slug = selectedProduct.slug;
  const variant = selectedProduct.variants?.[0];
  const qualitySettings = getQualityForSlug(slug);

  // Place state (from autocomplete)
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(initialPlace ?? null);

  // Display override state
  const [displayCity, setDisplayCity] = useState('');
  const [displayCountry, setDisplayCountry] = useState('');
  const [showOverrides, setShowOverrides] = useState(false);

  // Map config state
  const [theme, setTheme] = useState<ThemeId>('terracotta');
  const [distance, setDistance] = useState(DEFAULT_DISTANCE);
  const [font, setFont] = useState(DEFAULT_FONT);
  // A whole country or state picked in search: mapped with these options instead of a radius
  const [areaOptions, setAreaOptions] = useState<AreaOptionsValue>(DEFAULT_AREA_OPTIONS);
  const kind = areaKind(selectedPlace);

  // Preview state
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewCacheKey, setPreviewCacheKey] = useState<string | null>(null);
  // Where the preview is centred: a city pick snaps to the pre-fetched city's centre
  const [previewPoint, setPreviewPoint] = useState<{ lat: number; lon: number } | null>(null);
  // The country or state the preview maps, saved in the order so deck's print matches
  const [previewAreaId, setPreviewAreaId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Adding to cart state
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);

  // Refs for debounce and abort
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const canAddToCart = previewUrl && previewCacheKey && !isGenerating && !isAddingToCart && !addedToCart;

  // Auto-generate preview with debounce when values change
  useEffect(() => {
    if (!selectedPlace) {
      setPreviewUrl(null);
      setPreviewCacheKey(null);
      return;
    }

    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;
      const currentRequestId = ++requestIdRef.current;

      setIsGenerating(true);
      setError(null);

      try {
        const result = await requestMapPoster(
          {
            lat: selectedPlace.lat,
            lon: selectedPlace.lon,
            city: selectedPlace.city,
            country: selectedPlace.country,
            display_city: displayCity.trim() || undefined,
            display_country: displayCountry.trim() || undefined,
            theme,
            distance,
            font_family: font !== DEFAULT_FONT ? font : undefined,
            quality: qualitySettings.preview,
            snap: selectedPlace.isCity || undefined,
            ...(selectedPlace.areaType && areaRequestParams(selectedPlace, areaOptions)),
          },
          controller.signal
        );

        // Check if this request is still the latest
        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        if (!result.success || !result.presignedUrl) {
          setError(result.error || 'Failed to generate map. Please try again.');
          return;
        }

        setPreviewUrl(result.presignedUrl);
        setPreviewCacheKey(result.cacheKey || null);
        setPreviewPoint(
          result.lat !== undefined && result.lon !== undefined ? { lat: result.lat, lon: result.lon } : null
        );
        setPreviewAreaId(result.areaId ?? null);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        if (currentRequestId === requestIdRef.current) {
          setError('Network error. Please check your connection and try again.');
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsGenerating(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [selectedPlace, displayCity, displayCountry, theme, distance, font, areaOptions, qualitySettings.preview]);

  const handlePlaceSelect = (place: PlaceResult) => {
    setSelectedPlace(place);
    setError(null);
    setAddedToCart(false);
  };

  const handleAddToCart = async () => {
    if (!canAddToCart || !variant || !selectedPlace) return;

    setIsAddingToCart(true);
    setError(null);

    try {
      const metadata: MapPuzzleMetadata = {
        type: 'map-puzzle',
        previewUrl: previewUrl || '',
        previewCacheKey: previewCacheKey || '',
        config: {
          city: selectedPlace.city,
          country: selectedPlace.country,
          // The preview's centre, so deck's print of the order matches it
          lat: previewPoint?.lat ?? selectedPlace.lat,
          lon: previewPoint?.lon ?? selectedPlace.lon,
          displayCity: displayCity.trim() || undefined,
          displayCountry: displayCountry.trim() || undefined,
          theme,
          distance,
          font: font !== DEFAULT_FONT ? font : undefined,
          ...(previewAreaId && areaConfig(previewAreaId, areaOptions)),
          quality: qualitySettings.print,
        },
      };

      await addItem(
        variant.id,
        1,
        [{ key: 'mapPuzzle', value: stringifyMapPuzzleMetadata(metadata) }],
        true
      );

      const updatedCart = await refetch();

      setAddedToCart(true);
      onSuccess(updatedCart?.id || '');
    } catch (_err) {
      setError('Failed to add to cart. Please try again.');
    } finally {
      setIsAddingToCart(false);
    }
  };

  const cityDisplay = displayCity || selectedPlace?.city || '';
  const countryDisplay = displayCountry || selectedPlace?.country || '';

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Left Column: Configuration */}
      <div className="space-y-6">
        {/* Location */}
        <PlaceAutocomplete
          onPlaceSelect={handlePlaceSelect}
          disabled={isAddingToCart}
          initialPlace={initialPlace}
        />

        {/* Override text toggle */}
        <button
          type="button"
          onClick={() => setShowOverrides(!showOverrides)}
          className="text-secondary-1 hover:text-primary-1-80 flex items-center gap-1 text-sm font-medium"
          disabled={isAddingToCart}
        >
          <svg
            className={`h-4 w-4 transition-transform ${showOverrides ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          Customize displayed text
        </button>

        {showOverrides && (
          <div className="border-neutral-2-20 space-y-4 rounded-md border bg-neutral-2-5 p-4">
            <p className="text-neutral-2-60 text-xs">
              Override the text shown on the poster. Leave blank to use the detected names.
            </p>
            <div>
              <label htmlFor="displayCity" className="text-primary-2-80 block text-sm font-medium">
                Title
              </label>
              <input
                type="text"
                id="displayCity"
                value={displayCity}
                onChange={(e) => { setDisplayCity(e.target.value); setAddedToCart(false); }}
                disabled={isAddingToCart}
                placeholder={selectedPlace?.city || 'Custom city text'}
                className="border-neutral-2-20 focus:border-secondary-1 focus:ring-secondary-1 mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:bg-neutral-2-10"
              />
            </div>
            <div>
              <label htmlFor="displayCountry" className="text-primary-2-80 block text-sm font-medium">
                Subtitle
              </label>
              <input
                type="text"
                id="displayCountry"
                value={displayCountry}
                onChange={(e) => { setDisplayCountry(e.target.value); setAddedToCart(false); }}
                disabled={isAddingToCart}
                placeholder={selectedPlace?.country || 'Custom country text'}
                className="border-neutral-2-20 focus:border-secondary-1 focus:ring-secondary-1 mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:bg-neutral-2-10"
              />
            </div>
          </div>
        )}

        {/* Theme Selector */}
        <ThemeSelector selected={theme} onSelect={(t) => { setTheme(t); setAddedToCart(false); }} />

        {/* Font Selector */}
        <FontSelector
          selected={font}
          onSelect={(f) => { setFont(f as FontId); setAddedToCart(false); }}
          disabled={isAddingToCart}
        />

        {/* Radius for a city, or what to draw for a whole country or state */}
        {kind ? (
          <AreaOptions
            value={areaOptions}
            onChange={(options) => { setAreaOptions(options); setAddedToCart(false); }}
            kind={kind}
            disabled={isAddingToCart}
          />
        ) : (
          <DistanceSlider
            value={distance}
            onChange={(d) => { setDistance(d); setAddedToCart(false); }}
            disabled={isAddingToCart}
          />
        )}
      </div>

      {/* Right Column: Preview & Actions */}
      <div className="space-y-4">
        {/* Preview */}
        <MapPreview
          imageUrl={previewUrl}
          isLoading={isGenerating}
          aspectRatio={previewAspect(selectedPlace, qualitySettings.aspect)}
          error={error}
        />

        {previewUrl && selectedPlace && (
          <div className="bg-neutral-2-5 rounded-lg p-3">
            <div className="text-neutral-2-80 text-sm">
              <strong>Map:</strong> {mapSummary(selectedPlace, cityDisplay, countryDisplay, distance)}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {addedToCart ? (
          <div className="space-y-3">
            <div className="flex items-center justify-center gap-2 rounded-lg bg-green-50 p-4 text-green-700">
              <CheckCircleIcon className="h-6 w-6" />
              <span className="font-medium">Added to cart!</span>
            </div>
            <button
              className={cn(buttonVariants({ intent: 'primary' }), 'w-full')}
              onClick={onProceedToCheckout}
              type="button"
            >
              Proceed to Checkout
            </button>
          </div>
        ) : (
          <button
            className={cn(buttonVariants({ intent: 'primary' }), 'w-full')}
            onClick={handleAddToCart}
            disabled={!canAddToCart}
            type="button"
          >
            {isAddingToCart ? 'Adding to Cart...' : 'Add to Cart'}
          </button>
        )}

        {error && !isGenerating && (
          <div className="rounded-md bg-red-50 p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export function MapPuzzleFlowSheet({
  isOpen,
  onClose,
  products,
  params,
  initialSlug,
  initialPlace,
}: MapPuzzleFlowSheetProps) {
  const navigate = useNavigate();

  const [selectedSlug, setSelectedSlug] = useState<string | undefined>(initialSlug);
  const [checkoutId, setCheckoutId] = useState<string | null>(null);
  const [mapConfigured, setMapConfigured] = useState(false);

  const mapSectionRef = useRef<HTMLDivElement>(null);

  // Handle browser back button
  useEffect(() => {
    if (!isOpen) return;

    const handlePopState = () => {
      onClose();
    };

    window.history.pushState({ modal: 'map-sheet' }, '');
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, onClose]);

  const scrollToMap = useCallback(() => {
    mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleClose = () => {
    setSelectedSlug(initialSlug);
    setCheckoutId(null);
    setMapConfigured(false);
    onClose();
  };

  const handleProceedToCheckout = () => {
    onClose();
    navigate(urls.checkout.cart(params));
  };

  const selectedProduct = products.find((p) => p.slug === selectedSlug);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-x-0 bottom-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-y-full"
                enterTo="translate-y-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-y-0"
                leaveTo="translate-y-full"
              >
                <Dialog.Panel className="pointer-events-auto w-full">
                  <div className="flex h-[85vh] flex-col rounded-t-3xl bg-white shadow-xl">
                    {/* Header */}
                    <div className="border-neutral-2-10 flex items-center justify-between border-b px-6 py-4">
                      <Dialog.Title className="font-heading-condensed text-primary-2-80 text-xl leading-6 font-medium">
                        Create Your Map Puzzle
                      </Dialog.Title>
                      <button
                        onClick={handleClose}
                        className="hover:bg-neutral-2-10 rounded-full p-2"
                        aria-label="Close"
                      >
                        <XMarkIcon className="text-neutral-2-80 h-6 w-6" />
                      </button>
                    </div>

                    {/* Scrollable Content */}
                    <div className="flex-1 overflow-y-auto px-6 py-6">
                      <div className="space-y-8">
                        {/* Step 1: Select Puzzle */}
                        <StepSection
                          number={1}
                          title="Select Puzzle Type"
                          completed={!!selectedSlug}
                        >
                          <SelectPuzzleType
                            products={products}
                            selectedSlug={selectedSlug}
                            onSelect={setSelectedSlug}
                            scrollToMap={scrollToMap}
                          />
                        </StepSection>

                        {/* Step 2: Configure Map */}
                        {selectedSlug && selectedProduct && (
                          <div ref={mapSectionRef}>
                            <StepSection
                              number={2}
                              title="Design Your Map"
                              completed={mapConfigured}
                            >
                              <ConfigureMap
                                selectedProduct={selectedProduct}
                                params={params}
                                onSuccess={(id) => {
                                  setCheckoutId(id);
                                  setMapConfigured(true);
                                }}
                                onProceedToCheckout={handleProceedToCheckout}
                                initialPlace={initialPlace}
                              />
                            </StepSection>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
